var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AskOkMessage, DeleteModelOnDisconnectFacet, SetToolViewerVisibilityMessage, worldToLocalTransform, } from "flavr-common";
import { Matrix4 } from "flavr-math";
import { PickupModelMessage, PickupModelResponseMessage } from "@messages/attachment/AttachmentMessage";
import { AwaitScriptsActivationRequestMessage } from "@messages/scripting/ScriptMessages";
import { modelPool } from "@modules/model";
import { getLocalPlayerClientIdxOrThrow } from "@modules/player";
import { InteractionHandler } from "../../Interactions";
import { ToolBeltPickupAction } from "./ToolBeltPickupAction";
const TOOL_WORLD_TRANSFORM = new Matrix4();
const TOOL_PARENT_WORLD_TRANSFORM = new Matrix4();
const TOOL_LOCAL_TRANSFORM = new Matrix4();
export class ToolBeltPickupInteractionHandler extends InteractionHandler {
    constructor() {
        super(ToolBeltPickupAction.typestatic, [], [ToolBeltPickupAction.typestatic], []);
    }
    getActionDescription(params) {
        var _a;
        if (ToolBeltPickupAction.is(params)) {
            return (_a = params.description) !== null && _a !== void 0 ? _a : "";
        }
        return "";
    }
    performAction(params, context) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            if (!ToolBeltPickupAction.is(params))
                return;
            try {
                // Abort if the tool model that we want to pick up has already been deleted
                const toolModel = modelPool.getModel(params.params.srcModelUid);
                if (toolModel === undefined) {
                    console.warn("[interaction] Tool belt pickup failed because the tool model does not exist");
                    return;
                }
                // We create a clone of the tool that is visible to all peers and place it in the current room
                const pickupParentUid = (_a = params.parentUid) !== null && _a !== void 0 ? _a : (_b = context.controller) === null || _b === void 0 ? void 0 : _b.uid;
                const pickupRoom = (_c = modelPool.getCurrentRoom()) !== null && _c !== void 0 ? _c : modelPool.getRoot();
                const pickupModel = this.createSharedPickupModel(toolModel, pickupRoom, pickupParentUid);
                yield this.connection.askByMessageType(new AwaitScriptsActivationRequestMessage(pickupModel.getUID(), true, 2000), AskOkMessage);
                // Next we hide the tool belt, effectively deleting the tools in the tool belt
                this.connection.send(new SetToolViewerVisibilityMessage(false));
                // Next we pick the model up
                yield this.connection.askByMessageType(new PickupModelMessage(pickupModel.getUID(), {
                    controllerUid: pickupParentUid,
                    offset: params.params.offset,
                    dropOffMode: params.params.dropOffMode,
                    dropOffLayers: params.params.dropOffLayers,
                    dropOffButton: params.params.dropOffButton,
                    createTemporaryModel: false,
                    deleteOnDropOff: true,
                    showDropOffIcon: params.params.showDropOffIcon
                }), PickupModelResponseMessage);
            }
            catch (error) {
                console.error("Error in ToolBeltPickupInteractionHandler: ", error);
            }
        });
    }
    createSharedPickupModel(toolModel, pickupRoom, pickupParentUid) {
        // Create a clone of the tool model
        const cloneName = `pickup_${toolModel.getName()}_${String(pickupParentUid !== null && pickupParentUid !== void 0 ? pickupParentUid : toolModel.getUID())}`;
        const pickupModel = modelPool.cloneModel(toolModel, {
            parent: pickupRoom,
            name: cloneName,
            peer: true,
            allowOverwritingModel: true,
        });
        // cloneModel() copies the source tool's current visibility. The tool belt can hide the source tool at any
        // time based on head gaze angle (ToolViewerModule.updateVRToolBeltVisibility, VR-only), independent of this
        // pickup - if that fires around the same frame the pickup model would be born invisible, and nothing
        // afterwards would ever make it visible again. The picked up model is a standalone object now, so force it
        // visible regardless of whatever state the source happened to be in.
        pickupModel.setVisible(true, { peer: true });
        // We need to compute the local transform to the new parent based on the world transform of the original tool model
        toolModel.getWorldTransform(TOOL_WORLD_TRANSFORM);
        pickupRoom.getWorldTransform(TOOL_PARENT_WORLD_TRANSFORM);
        pickupModel.setTransform(worldToLocalTransform(TOOL_WORLD_TRANSFORM, TOOL_PARENT_WORLD_TRANSFORM, TOOL_LOCAL_TRANSFORM), { updateFacet: true, peer: true });
        // The picked up model should also be deleted when the player disconnects while holding the tool
        const clientIdx = getLocalPlayerClientIdxOrThrow();
        pickupModel.setFacet(new DeleteModelOnDisconnectFacet(clientIdx), { peer: true });
        modelPool.flushMessages();
        return pickupModel;
    }
}
